import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

// Define the interface for the context value
interface NavbarContextProps {
  isNavbarVisible: boolean;
  showNavbar: () => void;
  hideNavbar: () => void;
  toggleNavbarVisibility: () => void;
}

// Create the context with undefined as the default value
const NavbarContext = createContext<NavbarContextProps | undefined>(undefined);

// Custom hook that uses the context
export const useNavbar = (): NavbarContextProps => {
  const context = useContext(NavbarContext);
  if (!context) {
    throw new Error('useNavbar must be used within a NavbarProvider');
  }
  return context;
};

// Provider component that wraps your app or part of it
interface NavbarProviderProps {
  children: ReactNode;
}

export const NavbarProvider: React.FC<NavbarProviderProps> = ({ children }) => {
  const [isNavbarVisible, setNavbarVisible] = useState<boolean>(true);

  const showNavbar = useCallback(() => {
    setNavbarVisible(true);
  }, []);

  const hideNavbar = useCallback(() => {
    setNavbarVisible(false);
  }, []);

  const toggleNavbarVisibility = useCallback(() => {
    setNavbarVisible(prev => !prev);
  }, []);

  return (
    <NavbarContext.Provider
      value={{
        isNavbarVisible,
        showNavbar,
        hideNavbar,
        toggleNavbarVisibility,
      }}
    >
      {children}
    </NavbarContext.Provider>
  );
};
